import { SITE_NAME } from '../constants/config';

// Marks the tags we manage, so they can be swapped when the page changes.
export const MANAGED_HEAD_ATTRIBUTE = 'data-page-meta';

/**
 * The <head> tags for a page, as plain descriptions: { tag, attributes, content? }.
 * Used both when pre-rendering HTML files and when switching pages in the browser.
 */
export function buildHeadTags(meta) {
  const tags = [
    { tag: 'meta', attributes: { name: 'description', content: meta.description } },
    { tag: 'meta', attributes: { name: 'robots', content: meta.noIndex ? 'noindex, follow' : 'index, follow, max-image-preview:large' } },
    { tag: 'meta', attributes: { property: 'og:type', content: 'website' } },
    { tag: 'meta', attributes: { property: 'og:site_name', content: SITE_NAME } },
    { tag: 'meta', attributes: { property: 'og:title', content: meta.title } },
    { tag: 'meta', attributes: { property: 'og:description', content: meta.description } },
    { tag: 'meta', attributes: { property: 'og:image', content: meta.imageUrl } },
    { tag: 'meta', attributes: { property: 'og:image:width', content: '1200' } },
    { tag: 'meta', attributes: { property: 'og:image:height', content: '630' } },
    { tag: 'meta', attributes: { name: 'twitter:card', content: 'summary_large_image' } },
    { tag: 'meta', attributes: { name: 'twitter:title', content: meta.title } },
    { tag: 'meta', attributes: { name: 'twitter:description', content: meta.description } },
    { tag: 'meta', attributes: { name: 'twitter:image', content: meta.imageUrl } },
  ];
  if (meta.canonicalUrl) {
    tags.push({ tag: 'link', attributes: { rel: 'canonical', href: meta.canonicalUrl } });
    tags.push({ tag: 'meta', attributes: { property: 'og:url', content: meta.canonicalUrl } });
  }
  meta.schemas.forEach((schema) => {
    tags.push({ tag: 'script', attributes: { type: 'application/ld+json' }, content: JSON.stringify(schema) });
  });
  return tags;
}

const escapeHtml = (text) =>
  String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * The same tags as an HTML string, for the pre-rendered page files.
 */
export function renderHeadTagsToHtml(meta) {
  const tagsHtml = buildHeadTags(meta).map(({ tag, attributes, content }) => {
    const attributesHtml = Object.entries({ ...attributes, [MANAGED_HEAD_ATTRIBUTE]: '' })
      .map(([name, value]) => (value === '' ? name : `${name}="${escapeHtml(value)}"`))
      .join(' ');
    if (tag === 'script') return `<script ${attributesHtml}>${content.replace(/</g, '\\u003c')}</script>`;
    return `<${tag} ${attributesHtml}>`;
  });
  return [`<title>${escapeHtml(meta.title)}</title>`, ...tagsHtml].join('\n    ');
}

/**
 * Swaps the managed tags in the live document (after moving to another page).
 */
export function applyHeadTags(meta) {
  document.title = meta.title;
  document.head.querySelectorAll(`[${MANAGED_HEAD_ATTRIBUTE}]`).forEach((element) => element.remove());
  buildHeadTags(meta).forEach(({ tag, attributes, content }) => {
    const element = document.createElement(tag);
    Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
    element.setAttribute(MANAGED_HEAD_ATTRIBUTE, '');
    if (content) element.textContent = content;
    document.head.appendChild(element);
  });
}
